import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from '@reduxjs/toolkit';
import classNames from 'classnames';
import * as FormActions from '../../actions/actionCreators';
import type { RootState } from '../../store';

interface StepProps {
  isCurrent: boolean;
  isComplete: boolean;
  title: string;
  name: string;
  children: React.ReactNode;
  formActions: typeof FormActions;
  index: number;
  formType: string;
}

const Step: React.FC<StepProps> = ({
  formType,
  title,
  isComplete,
  isCurrent,
  children,
  name,
  index,
  formActions,
}) => {
  const scrollToRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isCurrent) {
      scrollToRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }
  }, [isCurrent]);

  const handleStepEdit = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isCurrent) {
      formActions.editForm(name, index, formType);
    }
  };

  const stepClass = classNames({
    step: true,
    'step--open': formType === 'contact' ? true : isCurrent,
    'step--complete': formType === 'contact' ? true : isComplete,
  });

  if (!isCurrent) {
    return null;
  }

  return (
    <div
      className={stepClass}
      onClick={handleStepEdit}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleStepEdit(e as any);
        }
      }}
      ref={scrollToRef}
      role="button"
      tabIndex={0}
      aria-label={`Step ${index + 1}: ${title}`}
    >
      <div className="step__header">
        <h2 className="step__title mt--0">{title}</h2>
        {isComplete ? (
          <i className="step__icon">
            <svg width={'32'} height={'26'} xmlns="http://www.w3.org/2000/svg">
              <path
                stroke="#69C35E"
                strokeWidth={'4.5'}
                d="M3 15.448L9.976 22 29 3"
                fill="none"
                fillRule={'evenodd'}
                strokeLinecap={'round'}
              />
            </svg>
          </i>
        ) : null}
      </div>
      <div className="step__content">
        {React.cloneElement(
          React.Children.only(children as React.ReactElement),
          {
            name,
            index,
          }
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState, props: Pick<StepProps, 'name'>) => ({
  isCurrent: state.forms[props.name].isCurrent,
  isComplete: state.forms[props.name].isComplete,
});

const mapDispatchToProps = (dispatch: any) => ({
  formActions: bindActionCreators(FormActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(Step);
